import type { FC } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AddIcon,
  alpha,
  Avatar,
  Box,
  CheckIcon,
  Chip,
  Divider,
  IconButton,
  KeyboardArrowDownIcon,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@/shared/ui/mui';
import { useShopExpansionEligibility } from '@/features/shop-registration/hooks/useShopExpansionEligibility';
import type { BootstrapShop } from '@/shared/api/bootstrap';

interface ShopSwitcherProps {
  shops: BootstrapShop[];
  currentShopSlug?: string;
}

/**
 * Shop Switcher Component
 * Displays current shop and allows providers to switch between shops.
 * Shown in dashboard app bar for multi-shop owners.
 */
export const ShopSwitcher: FC<ShopSwitcherProps> = ({
  shops,
  currentShopSlug,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const {
    canAddShop,
    slotLabel,
    isLoading: isExpansionLoading,
  } = useShopExpansionEligibility();
  const showAddLocation = !isExpansionLoading &&
    canAddShop;

  if (shops.length === 0) {
    return null;
  }

  // Display-only fallback: if the URL slug isn't in the shops list
  // (stale cache, slug drift, etc.) show *some* label rather than
  // nothing. Navigation choices are made elsewhere — this `shops[0]`
  // never drives a redirect, only the trigger button text.
  const currentShop = shops.find(s => s.slug === currentShopSlug) || shops[0];
  const displayName = currentShop?.name || 'Shop';

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectShop = (shop: BootstrapShop) => {
    handleClose();
    navigate(`/shops/${shop.slug}/dashboard`);
  };

  const handleAddShop = () => {
    handleClose();
    navigate('/provider/setup?mode=add-shop');
  };

  return (
    <>
      <IconButton
        onClick={shops.length > 1 ? handleClick : undefined}
        size="small"
        disableRipple={shops.length <= 1}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          cursor: shops.length > 1 ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: shops.length > 1
              ? alpha(theme.palette.common.black, 0.04)
              : 'transparent',
          },
        }}
      >
        <Avatar
          src={currentShop?.logoUrl || undefined}
          sx={{
            width: 30,
            height: 30,
            backgroundColor: theme.palette.primary.main,
            fontSize: '0.8rem',
          }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
        <Typography
          variant="body2"
          sx={{
            display: { xs: 'none', sm: 'block' },
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {displayName}
        </Typography>
        {shops.length > 1 && (
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              color: theme.palette.text.secondary,
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        )}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: { minWidth: 260, mt: 1 },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Your Shops
          </Typography>
        </Box>

        {shops.map(shop => {
          const isCurrent = shop.slug === currentShopSlug;
          return (
            <MenuItem
              key={shop.id}
              onClick={() => handleSelectShop(shop)}
              selected={isCurrent}
              sx={{ py: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Avatar
                  src={shop.logoUrl || undefined}
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: theme.palette.primary.main,
                    fontSize: '0.75rem',
                  }}
                >
                  {shop.name.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={shop.name}
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <Chip
                    label={shop.role}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem', mt: 0.5 }}
                  />
                }
              />
              {isCurrent && (
                <CheckIcon sx={{ fontSize: 18, color: 'primary.main', ml: 1 }} />
              )}
            </MenuItem>
          );
        })}

        {showAddLocation && [
          <Divider key="add-shop-divider" />,
          <MenuItem key="add-shop" onClick={handleAddShop} sx={{ py: 1.5 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <AddIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            </ListItemIcon>
            <ListItemText primary="Add location" secondary={slotLabel} />
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
};
